import React, { memo, useMemo } from 'react';
import { Polyline } from 'react-google-maps';
import { filterTrailByTimeRange } from 'config/trailOptions';
import { getVehicleColor, getTrailPointColor } from 'utils/colorPalette';

interface Asset {
  id: number;
  gtsId: string;
  name: string;
  lat: number;
  lng: number;
  statusCodeColor: string;
  trail: Array<{
    lat: number;
    lng: number;
    timestamp: number;
  }>;
  visible?: boolean;
}

interface TrailPolylinesProps {
  assets: Asset[];
  trailsEnabled: boolean;
  colorTrailsEnabled: boolean;
  colorTrailsMode: 'vehicle' | 'length';
  activeTrailOption: any;
  hoveredAssetId: number | null;
}

function TrailPolylinesComponent({
  assets,
  trailsEnabled,
  colorTrailsEnabled,
  colorTrailsMode,
  activeTrailOption,
  hoveredAssetId,
}: TrailPolylinesProps) {
  const trailCache = useMemo(() => new Map<string, any>(), []);
  const currentTime = useMemo(() => Date.now(), []);

  const getMemoizedTrailSegments = useMemo(() => {
    return (asset: Asset, filteredTrail: any[], colorTrailsMode: string, currentTime: number) => {
      const cacheKey = `${asset.gtsId}-${colorTrailsMode}-${filteredTrail.length}-${filteredTrail[0]?.timestamp}-${
        filteredTrail[filteredTrail.length - 1]?.timestamp
      }`;

      if (trailCache.has(cacheKey)) {
        return trailCache.get(cacheKey);
      }

      if (colorTrailsMode === 'length') {
        const segments: Array<{ path: Array<{ lat: number; lng: number }>; color: string }> = [];

        for (let i = 0; i < filteredTrail.length - 1; i++) {
          const currentPoint = filteredTrail[i];
          const nextPoint = filteredTrail[i + 1];

          const pointAgeMs = currentTime - currentPoint.timestamp;
          const pointAgeHours = pointAgeMs / (1000 * 60 * 60);
          const color = getTrailPointColor(pointAgeHours);

          segments.push({
            path: [
              { lat: currentPoint.lat, lng: currentPoint.lng },
              { lat: nextPoint.lat, lng: nextPoint.lng },
            ],
            color,
          });
        }

        trailCache.set(cacheKey, segments);
        return segments;
      }

      const path = filteredTrail.map(({ lat, lng }) => ({ lat, lng }));
      trailCache.set(cacheKey, path);
      return path;
    };
  }, [trailCache]);

  const visibleAssets = useMemo(() => {
    return assets.filter((asset) => asset.visible !== false);
  }, [assets]);

  const polylines = useMemo(() => {
    if (!trailsEnabled || visibleAssets.length === 0) {
      return [];
    }

    const hasHoveredAsset = hoveredAssetId !== null;

    return visibleAssets
      .map((asset, assetIndex) => {
        const isHovered = hoveredAssetId === asset.id;
        const filteredTrail = filterTrailByTimeRange(asset.trail, activeTrailOption);

        if (!filteredTrail || filteredTrail.length < 2) return null;

        if (colorTrailsEnabled && colorTrailsMode === 'length') {
          const segments = getMemoizedTrailSegments(asset, filteredTrail, colorTrailsMode, currentTime);

          return segments.map((segment: any, segmentIndex: number) => (
            <Polyline
              key={`${asset.gtsId}-trail-segment-${segmentIndex}`}
              path={segment.path}
              options={{
                strokeColor: segment.color,
                strokeWeight: isHovered ? 4 : hasHoveredAsset ? 2 : 3,
                strokeOpacity: isHovered ? 1 : hasHoveredAsset ? 0.5 : 1,
              }}
            />
          ));
        } else {
          let color = '#4285F4';

          if (colorTrailsEnabled && colorTrailsMode === 'vehicle') {
            color = getVehicleColor(assetIndex);
          }

          const path = getMemoizedTrailSegments(asset, filteredTrail, colorTrailsMode, currentTime);

          return (
            <Polyline
              key={`${asset.gtsId}-trail`}
              path={path}
              options={{
                strokeColor: color,
                strokeWeight: isHovered ? 4 : hasHoveredAsset ? 2 : 3,
                strokeOpacity: isHovered ? 1 : hasHoveredAsset ? 0.5 : 1,
              }}
            />
          );
        }
      })
      .flat()
      .filter(Boolean);
  }, [
    trailsEnabled,
    visibleAssets,
    colorTrailsEnabled,
    colorTrailsMode,
    activeTrailOption,
    hoveredAssetId,
    getMemoizedTrailSegments,
    currentTime,
  ]);

  return <>{polylines}</>;
}

export const TrailPolylines = memo(TrailPolylinesComponent);